import axios from "axios";

import {
  preValidateCardPaymentMethod,
  tokenizeCard,
} from "../services/api";
import { brandFromPan, last4 as last4Of } from "./card-brand";
import { isVgsConfigUnavailable } from "./tokenization-config";
import type { IBillingAddress } from "../interfaces/payment-method";

export type FundingCardValidationErrorCode =
  | "CARD_VALIDATION_FAILED"
  | "CARD_UNDER_REVIEW"
  | "CARD_PULL_NOT_SUPPORTED"
  | "CARD_CORRIDOR_CONTEXT_REQUIRED"
  | "VGS_CONFIG_UNAVAILABLE"
  | "GENERIC";

export class FundingCardValidationError extends Error {
  readonly code: FundingCardValidationErrorCode;

  constructor(code: FundingCardValidationErrorCode, message?: string) {
    super(message ?? code);
    this.name = "FundingCardValidationError";
    this.code = code;
  }
}

export interface PreValidateFundingCardInput {
  cardholderName: string;
  pan: string;
  expiry: string;
  cvv: string;
  billingAddress: IBillingAddress;
  sourceCountry: string;
  destinationCountry: string;
}

export interface PreValidateFundingCardResult {
  validationId: string;
  cvvToken: string;
  numberToken: string;
  brand: string;
  last4: string;
  productClass?: "DEBIT" | "CREDIT" | "PREPAID" | "UNKNOWN";
  fundingMethod?: "DEBIT_CARD" | "CREDIT_CARD";
}

export const preValidateFundingCard = async (
  linkId: string,
  card: PreValidateFundingCardInput
): Promise<PreValidateFundingCardResult> => {
  const cleanedPan = card.pan.replace(/\D/g, "");
  const normalizedBrand = brandFromPan(cleanedPan);
  const last4 = last4Of(cleanedPan);

  const [expiryMonth, expiryYearShort] = card.expiry.split("/");
  const expiryYear = expiryYearShort ? `20${expiryYearShort}` : "";

  let tokenized;
  try {
    tokenized = await tokenizeCard(cleanedPan, card.cvv);
  } catch (error) {
    if (isVgsConfigUnavailable(error)) {
      throw new FundingCardValidationError("VGS_CONFIG_UNAVAILABLE");
    }
    throw new FundingCardValidationError("GENERIC", "Failed to tokenize card");
  }

  const { numberToken, cvvToken } = tokenized;

  const cardPayload = {
    tokenizedCardNumber: numberToken,
    tokenizedCvv: cvvToken,
    last4,
    brand: normalizedBrand,
    expiryMonth,
    expiryYear,
    cardholderName: card.cardholderName.trim(),
    billingAddress: {
      street1: card.billingAddress.street1.trim(),
      street2: card.billingAddress.street2?.trim() || undefined,
      city: card.billingAddress.city.trim(),
      state: card.billingAddress.state.trim().toUpperCase(),
      zipCode: card.billingAddress.zipCode.trim(),
      country: card.billingAddress.country.trim().toUpperCase() || "US",
    },
    sourceCountry: card.sourceCountry.trim().toUpperCase(),
    destinationCountry: card.destinationCountry.trim().toUpperCase(),
  };

  try {
    const pre = await preValidateCardPaymentMethod(linkId, cardPayload);
    if (!pre?.validationId) {
      throw new FundingCardValidationError("GENERIC");
    }
    return {
      validationId: pre.validationId,
      cvvToken,
      numberToken,
      brand: cardPayload.brand,
      last4,
      productClass: pre.productClass,
      fundingMethod: pre.fundingMethod,
    };
  } catch (error) {
    if (error instanceof FundingCardValidationError) throw error;

    if (axios.isAxiosError(error)) {
      const code = error.response?.data?.error?.code as string | undefined;
      if (
        code === "CARD_VALIDATION_FAILED" ||
        code === "CARD_UNDER_REVIEW" ||
        code === "CARD_PULL_NOT_SUPPORTED" ||
        code === "CARD_CORRIDOR_CONTEXT_REQUIRED"
      ) {
        throw new FundingCardValidationError(code);
      }
    }

    throw new FundingCardValidationError("GENERIC");
  }
};
